import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, XCircle, ShieldAlert } from "lucide-react";
import { TypeBadge, StatusBadge } from "../components/MrfBits";
import { inputCls } from "../hooks/useMasterMaps";

const BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function WaApprove() {
  const { reqId } = useParams();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [ctx, setCtx] = useState(null);
  const [error, setError] = useState("");
  const [done, setDone] = useState(null);
  const [rejecting, setRejecting] = useState(false);
  const [remarks, setRemarks] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`${BASE}/mrf/wa-context/${reqId}`, { params: { token } })
      .then(({ data }) => setCtx(data))
      .catch((e) => setError(e?.response?.data?.detail || "Invalid or expired approval link"));
  }, [reqId, token]);

  const act = async (action) => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${BASE}/mrf/${reqId}/wa-action`, { token, action, remarks });
      setDone(data);
    } catch (e) {
      setError(typeof e?.response?.data?.detail === "string" ? e.response.data.detail : "Action failed");
    } finally { setBusy(false); }
  };

  const req = ctx?.req;
  return (
    <div className="min-h-screen bg-[#F5F6F8]" data-testid="wa-approve-page">
      <header className="bg-[#0E2240] px-5 py-4">
        <div className="font-serif text-xl text-white">HIRE<span className="text-[#BF9B30]">360</span></div>
        <div className="text-[10px] uppercase tracking-[0.2em] text-white/50 mt-0.5">One-tap approval · Incheon Motors</div>
      </header>
      <div className="max-w-md mx-auto px-4 py-8">
        {error && (
          <div data-testid="wa-error" className="bg-white border border-[#BB162B]/40 rounded-sm p-6 text-center">
            <ShieldAlert className="mx-auto text-[#BB162B]" size={32} />
            <div className="mt-3 text-sm text-[#0E2240]">{error}</div>
          </div>
        )}
        {done && (
          <div data-testid="wa-done" className="bg-white border border-[#1B7F3B]/40 rounded-sm p-6 text-center">
            {done.action === "Approve"
              ? <CheckCircle2 className="mx-auto text-[#1B7F3B]" size={36} />
              : <XCircle className="mx-auto text-[#BB162B]" size={36} />}
            <div className="mt-3 font-serif text-lg text-[#0E2240]">{done.req_no} {done.action === "Approve" ? "approved" : "rejected"}</div>
            <div className="text-xs text-[#6B7280] mt-1">Recorded as {done.approver} via WhatsApp link</div>
          </div>
        )}
        {!error && !done && !ctx && <div className="text-center text-sm text-[#6B7280] py-12">Verifying approval link…</div>}
        {!error && !done && req && (
          <div className="bg-white border border-gray-200 rounded-sm">
            <div className="p-5 border-b border-gray-100">
              <div className="flex items-center justify-between">
                <div className="text-xs font-semibold text-[#0E2240]">{req.req_no}</div>
                <StatusBadge status={req.status} />
              </div>
              <div className="font-serif text-xl text-[#0E2240] mt-2">{req.positions_count} × {req.desig_name}</div>
              <div className="text-sm text-[#6B7280]">{req.branch_name} · {req.dept_name}</div>
              <div className="mt-3 flex items-center gap-2">
                <TypeBadge type={req.requisition_type} forced={req.forced_beyond} />
                <span className="text-[10px] uppercase tracking-wide text-[#6B7280]">{req.priority} priority</span>
              </div>
              {req.replacing_emp_name && (
                <div className="mt-2 text-xs text-[#6B7280]">Replacing {req.replacing_emp_name} ({req.replacing_emp_code})</div>
              )}
              <p className="mt-3 text-sm text-[#0E2240] leading-snug">{req.justification}</p>
              <div className="mt-3 text-xs text-[#6B7280]">
                Pending at <span className="font-medium text-[#0E2240]">{req.current_stage}</span>
                {ctx.is_current_owner
                  ? <> · with you as {ctx.approver_name}</>
                  : <> · you are signed as {ctx.approver_name}</>}
              </div>
            </div>
            {ctx.is_current_owner ? (
              <div className="p-5 space-y-3">
                {rejecting && (
                  <textarea data-testid="wa-reject-remarks" className={`${inputCls} min-h-[70px]`} placeholder="Rejection reason (mandatory)"
                    value={remarks} onChange={(e) => setRemarks(e.target.value)} />
                )}
                <div className="grid grid-cols-2 gap-3">
                  {!rejecting ? (
                    <>
                      <button data-testid="wa-approve-btn" disabled={busy} onClick={() => act("Approve")}
                        className="py-3 bg-[#1B7F3B] text-white text-sm font-medium rounded-sm hover:bg-[#166832] transition-colors duration-200 disabled:opacity-50">
                        Approve
                      </button>
                      <button data-testid="wa-reject-btn" disabled={busy} onClick={() => setRejecting(true)}
                        className="py-3 border border-[#BB162B] text-[#BB162B] text-sm font-medium rounded-sm hover:bg-[#BB162B]/5 transition-colors duration-200">
                        Reject
                      </button>
                    </>
                  ) : (
                    <>
                      <button data-testid="wa-reject-confirm" disabled={busy || !remarks.trim()} onClick={() => act("Reject")}
                        className="py-3 bg-[#BB162B] text-white text-sm font-medium rounded-sm hover:bg-[#a01324] transition-colors duration-200 disabled:opacity-50">
                        Confirm reject
                      </button>
                      <button onClick={() => setRejecting(false)}
                        className="py-3 border border-gray-300 text-sm rounded-sm hover:bg-gray-50 transition-colors duration-200">
                        Back
                      </button>
                    </>
                  )}
                </div>
              </div>
            ) : (
              <div data-testid="wa-not-owner" className="p-5 text-sm text-[#6B7280]">
                This requisition is not pending with you right now (status: {req.status}).
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
